import { createSlice } from "@reduxjs/toolkit";
import {
  DEFAULT_TEST_SETTINGS,
  normalizeTestSettings,
} from "../constants/testSettings";

const initialState = {
  isAuthenticated: false,
  tokenInvalidated: false,
  userData: {
    theme: "",
    time_zone: "",
    hide_warning: "",
    CompletedTests: [],
    IncompleteTests: [],
    ReviewHighYield: { HighYieldQuestions: [], MarkedQuestions: [] },
    category: [],
    password: "",
    //Create Test defaults. Independent: a test can be tutor, timed, both or neither.
    tutor_mode: false,
    timed_mode: true,
    test_settings: { ...DEFAULT_TEST_SETTINGS },
    //Create Test page selections; null until the user changes one (page uses its defaults)
    create_test_settings: null,
    tooth_notation: "",
  },
  uiState: {
    sidebarHidden: false,
  },
  modelFetching: {
    test: { buffer: undefined, progress: 0, loading: false },
  },
};
const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setUserData(state, action) {
      //Setting global variables for the user here after they login.
      state.userData = action.payload.user;
      //Settings bar values come back as null (never saved) or a partial object, always store a full one
      state.userData.test_settings = normalizeTestSettings(
        action.payload.user?.test_settings,
      );
      //Older backends only send test_mode ("Tutor" / "Timed"); every test was timed then
      if (typeof state.userData.tutor_mode !== "boolean") {
        state.userData.tutor_mode = action.payload.user?.test_mode === "Tutor";
      }
      if (typeof state.userData.timed_mode !== "boolean") {
        state.userData.timed_mode = true;
      }
      state.userData.password = action.payload.password;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.tokenInvalidated = action.payload.tokenInvalidated;
    },
    setUserDataTheme(state, action) {
      state.userData.theme = action.payload.theme;
    },
    setUserDataTimeZone(state, action) {
      state.userData.time_zone = action.payload.time_zone;
    },
    setUserDataWarnings(state, action) {
      state.userData.hide_warning = action.payload.hide_warning;
    },
    //payload: { tutor_mode: boolean, timed_mode: boolean }
    setTestMode(state, action) {
      state.userData.tutor_mode = action.payload.tutor_mode;
      state.userData.timed_mode = action.payload.timed_mode;
    },
    setTestSettings(state, action) {
      state.userData.test_settings = normalizeTestSettings(
        action.payload.test_settings,
      );
    },
    //payload: { question_modes, num_questions, excluded_subjects }
    setCreateTestSettings(state, action) {
      state.userData.create_test_settings = action.payload;
    },
    setUnauthenticated(state) {
      state.tokenInvalidated = true;
    },
    hideSidebar(state) {
      state.uiState.sidebarHidden = true;
    },
    showSidebar(state) {
      state.uiState.sidebarHidden = false;
    },
    setExamHistory(state, action) {
      state.userData.CompletedTests = action.payload.CompletedTests;
      state.userData.IncompleteTests = action.payload.IncompleteTests;
    },
    setLogout(state) {
      state.userData = {
        theme: "",
        time_zone: "",
        hide_warning: "",
        CompletedTests: [],
        IncompleteTests: [],
        ReviewHighYield: { HighYieldQuestions: [], MarkedQuestions: [] },
        category: [],
        password: "",
        tutor_mode: false,
        timed_mode: true,
        test_settings: { ...DEFAULT_TEST_SETTINGS },
        create_test_settings: null,
        tooth_notation: "",
      };
      state.isAuthenticated = false;
      state.tokenInvalidated = true;
    },
    setModelProgress(state, action) {
      state.modelFetching[action.payload.key].progress =
        action.payload.progress;
      state.modelFetching[action.payload.key].loading = action.payload.loading;
    },
    setModelBuffer(state, action) {
      if (state.modelFetching[action.payload.key] == undefined) {
        state.modelFetching[action.payload.key] = {};
      }
      state.modelFetching[action.payload.key].buffer = action.payload.buffer;
      state.modelFetching[action.payload.key].progress =
        action.payload.progress;
      state.modelFetching[action.payload.key].loading = action.payload.loading;
    },
    setReviewHighYield(state, action) {
      state.userData.ReviewHighYield.HighYieldQuestions =
        action.payload.HighYieldQuestions;
      state.userData.ReviewHighYield.MarkedQuestions =
        action.payload.MarkedQuestions;
    },
    setUserCategories(state, action) {
      state.userData.category = [...action.payload.categories];
    },
    setUserDataToothNotation: (state, action) => {
      state.userData.tooth_notation = action.payload.tooth_notation;
    },
  },
});

export const {
  setUserData,
  setUserDataTheme,
  setUserDataTimeZone,
  setUnauthenticated,
  hideSidebar,
  showSidebar,
  setExamHistory,
  setUserDataWarnings,
  setLogout,
  setModelProgress,
  setModelBuffer,
  setReviewHighYield,
  setUserCategories,
  setTestMode,
  setTestSettings,
  setCreateTestSettings,
  setUserDataToothNotation,
} = loginSlice.actions;
export default loginSlice.reducer;
export const getUserData = (state) => state.user.userData;
export const getUserState = (state) => state.user;
export const getUserCategories = (state) => state.user.userData.category;
export const getUserAuthenticated = (state) => state.user.isAuthenticated;
export const getUserDataTheme = (state) => state.user.userData.theme;
export const getUserDataTimeZone = (state) => state.user.userData.time_zone;
export const getTokenInvalidated = (state) => state.user.tokenInvalidated;
export const getSidebarHidden = (state) => state.user.uiState.sidebarHidden;
export const getUserDataWarnings = (state) => state.user.userData.hide_warning;
export const getModelFetchingData = (state) => state.user.modelFetching;
export const getReviewHighYield = (state) =>
  state.user.userData.ReviewHighYield;
export const getTestSettings = (state) => state.user.userData.test_settings;
